import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { FaCheckCircle, FaRegClock, FaHome, FaRedo, FaTrashAlt } from 'react-icons/fa';
import {
  PageContainer,
  Header,
  PlansScroll,
  PlanCard,
  PlanButton,
  DetailsButton,
  PackageRow,
  PackageCell,
  PackageValue,
  SectionBox,
  StatusBadge,
  IconCircle,
  PackageLabel,
  PackageActions,
  RenewButton,
  DetailsModalOverlay,
  DetailsModalBox,
  CloseModalBtn,
  AlertOverlay,
  AlertBox,
  AlertTitle,
  AlertDescription,
  AlertOkButton
} from './styles';

function PackageDetailsModal({ pkg, onClose, onRenew, showRenewButtons = false }) {
  if (!pkg) return null;
  return (
    <DetailsModalOverlay>
      <DetailsModalBox>
        <CloseModalBtn onClick={onClose} title="Fechar">&times;</CloseModalBtn>
        <h2 style={{ marginBottom: 10, color: '#34574A', fontWeight: 700 }}>Detalhes do Pacote</h2>
        <div style={{ marginBottom: 14 }}><b>Nome:</b> {pkg.nome}</div>
        <div style={{ marginBottom: 7 }}><b>Status:</b> <span style={{ color: pkg.status === 'Ativo' ? '#388e3c' : '#c62828', fontWeight: 600 }}>{pkg.status}</span></div>
        <div style={{ marginBottom: 7 }}><b>Validade:</b> {pkg.validade}</div>
        <div style={{ marginBottom: 7 }}><b>Data de aquisição:</b> {pkg.dataAquisicao}</div>
        <div style={{ marginBottom: 14 }}><b>Utilizados:</b> {pkg.usados} de {pkg.quantidade}</div>
        {pkg.status === 'Expirado' && showRenewButtons && (
          <RenewButton onClick={() => { onRenew(pkg); onClose(); }} style={{ marginTop: 10 }}><FaRedo /> Renovar</RenewButton>
        )}
      </DetailsModalBox>
    </DetailsModalOverlay>
  );
}

const MyPackages = () => {
  const location = useLocation();
  const showRenewButtons = false; // ocultar botão de renovar por padrão
  const showDeleteButtons = false; // ocultar lixeiras por padrão
  const PAGE_SIZE = 20;
  const [modalPkg, setModalPkg] = useState(null);
  const [plans, setPlans] = useState({});
  const [selectedTab, setSelectedTab] = useState('');
  const [role, setRole] = useState('PARTICULAR');
  const [acquiredPackages, setAcquiredPackages] = useState([]);
  const [Loading, setLoading] = useState(true);
  const [modalAlert, setModalAlert] = useState(null);
  const [AnunciosCount, setAnunciosCount] = useState({}); // Cache de contagem por pacote
  const [confirmModal, setConfirmModal] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [expiredPage, setExpiredPage] = useState(1);
  const [cancelledPage, setCancelledPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const normalizePlanQuantity = (plano) => {
    const qty = Number(plano?.quantidadePLANO ?? plano?.quantidade);
    if (!Number.isFinite(qty)) return qty;
    const rawName = String(plano?.nomePLANO || plano?.title || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const tipo = String(plano?.tipoPlano?.planoTIPOPLANO || plano?.tipo || '').toLowerCase();
    const isFerias = rawName.includes('ferias') || tipo.includes('ferias');
    const isDestaque = rawName.includes('destaque') || tipo.includes('destaque');
    const days = Number(plano?.tempoPLANO || 0);

    // Para planos Destaque, retornar a quantidade diretamente da API
    if (isDestaque) {
      return qty;
    }

    if (isFerias) {
      if (days === 30 || rawName.includes('1 mes') || rawName.includes('1 mês')) return 1;
      if (days === 90 || rawName.includes('3 meses')) return 1;
      if (days === 180 || rawName.includes('6 meses')) return 1;
      if (days === 365 || rawName.includes('12 meses') || rawName.includes('1 ano')) {
        if (rawName.includes('1 imovel') || rawName.includes('>1')) return 1;
        return 12;
      }
    }

    if (qty === 13 && rawName.includes('plano 12 meses')) return 12;
    return qty;
  };

  const buildPlanSubtitle = useCallback((plano) => {
    const qty = normalizePlanQuantity(plano);
    if (Number.isFinite(qty) && qty > 0) {
      const noun = qty === 1 ? 'Imóvel' : 'Imóveis';
      return `${qty} ${noun}`;
    }
    return plano?.subtitle || '—';
  }, []);

  const showAlert = (title, description) => {
    setModalAlert({ title, description });
  };

  const closeAlert = () => {
    setModalAlert(null);
  };

  const openConfirm = (title, description, onConfirm) => {
    setConfirmModal({ title, description, onConfirm });
  };

  const closeConfirm = () => setConfirmModal(null);

  useEffect(() => {
    // Detecta o perfil do usuário a partir do localStorage
    try {
      const rawUser = localStorage.getItem('user');
      const rawCurrentUser = localStorage.getItem('current_user');
      const authRole = localStorage.getItem('auth_role');
      const authToken = localStorage.getItem('auth_token');
      const norm = (s) => (s || '').toString().trim().toUpperCase();
      const tokenize = (s) => norm(s).split(/[^A-Z]+/).filter(Boolean);
      const adminAliases = new Set(['ADMIN', 'ADMINISTRADOR', 'ADMINISTRATOR', 'SUPERADMIN', 'SUPER_ADMIN', 'ROOT']);

      const authTokens = tokenize(authRole || '');
      const userObj = rawUser ? JSON.parse(rawUser) : {};
      const currentUserObj = rawCurrentUser ? JSON.parse(rawCurrentUser) : {};
      const userRoleRaw = userObj.perfil || userObj.perfilUSUARIO || userObj.role || userObj.tipo || userObj.nivel || userObj.papel || '';
      const currentUserRoleRaw = currentUserObj.perfil || currentUserObj.perfilUSUARIO || currentUserObj.role || currentUserObj.tipo || currentUserObj.nivel || currentUserObj.papel || '';
      const userTokens = tokenize(userRoleRaw);
      const currentUserTokens = tokenize(currentUserRoleRaw);

      // Decodifica JWT (base64url) e tenta extrair possíveis campos de role
      let jwtTokens = [];
      try {
        if (authToken && authToken.split('.').length === 3) {
          const b64url = authToken.split('.')[1];
          const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(b64url.length / 4) * 4, '=');
          const payload = JSON.parse(atob(b64));
          const jwtRoleRaw = (payload.role || payload.perfil || payload.tipo || payload.nivel || payload.papel || '').toString();
          jwtTokens = tokenize(jwtRoleRaw);
          // Se o JWT já trouxe role válida (ADMIN/PROFISSIONAL/PARTICULAR), prioriza imediatamente
          const primary = jwtTokens.find(t => ['ADMIN','PROFISSIONAL','PARTICULAR'].includes(t));
          if (primary) {
            setRole(primary);
            try { console.log('[MyPackages] Role set from JWT payload:', payload); } catch (e) { /* ignore */ }
            // não retornar aqui; seguimos para carregar pacotes adquiridos
          }

        }
      } catch (e) { /* ignore */ }

      const anyAdmin = [...authTokens, ...userTokens, ...currentUserTokens, ...jwtTokens].some(t => adminAliases.has(t));
      if (anyAdmin) {
        setRole('ADMIN');
        try { console.log('[MyPackages] Detected role ADMIN via tokens', { authTokens, userTokens, currentUserTokens, jwtTokens }); } catch (e) { /* ignore */ }
      } else {
        // mapear explicitamente para os 5 tipos oficiais
        const types = ['PROFISSIONAL','EMPREENDIMENTO','PARTICULAR','USUARIO'];
        const fromTokens = (tokens) => {
          for (const t of tokens) {
            if (types.includes(t)) return t;
          }
          return '';
        };
        const resolved = fromTokens(authTokens) || fromTokens(userTokens) || fromTokens(currentUserTokens) || fromTokens(jwtTokens) || 'PARTICULAR';
        setRole(resolved);
        try { console.log('[MyPackages] Detected role non-admin:', { resolved, authTokens, userTokens, currentUserTokens, jwtTokens }); } catch (e) { /* ignore */ }
      }
    } catch (e) { /* ignore */ }

    // Função para buscar contagem real de anúncios por pacote
    const fetchAnunciosCount = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${import.meta.env.VITE_URL_API}/imoveis/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json'
          }
        });
        if (res.ok) {
          const data = await res.json();
          const lista = data?.imoveis || [];
          const contagem = {};
          lista.forEach(anuncio => {
            if (anuncio.pacoteIMOVEL) {
              const pacoteKey = String(anuncio.pacoteIMOVEL);
              contagem[pacoteKey] = (contagem[pacoteKey] || 0) + 1;
            }
          });
          setAnunciosCount(contagem);
          return contagem;
        }
      } catch (error) {
        console.error('Erro ao buscar contagem de anúncios:', error);
      }
      return {};
    };

    const fetchPackages = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const [checkoutsRes, faturasRes] = await Promise.all([
          fetch(`${import.meta.env.VITE_URL_API}/checkouts`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          }),
          fetch(`${import.meta.env.VITE_URL_API}/faturas`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          })
        ]);
        const checkoutsDataRaw = await checkoutsRes.json();
        const faturasDataRaw = await faturasRes.json().catch(() => []);

        // Backend retorna array diretamente ou objeto com propriedade "checkouts"
        const checkoutsData = Array.isArray(checkoutsDataRaw) ? checkoutsDataRaw : (checkoutsDataRaw.checkouts || checkoutsDataRaw);
        const faturasData = Array.isArray(faturasDataRaw) ? faturasDataRaw : (faturasDataRaw.faturas || faturasDataRaw);

        // Mapear faturas por checkout para recuperar data correta de aquisição
        const faturasByCheckout = {};
        (faturasData || []).forEach(f => {
          const checkoutId = f?.checkout?.codigoCHECKOUT ?? f?.checkoutFATURA;
          if (checkoutId != null) {
            faturasByCheckout[String(checkoutId)] = f;
          }
        });

        // Adicionar checkouts que estão nas faturas mas não em /checkouts (workaround)
        const checkoutMap = new Map();
        (checkoutsData || []).forEach(c => {
          const id = c?.codigoCHECKOUT;
          if (id != null) checkoutMap.set(String(id), c);
        });
        (faturasData || []).forEach(f => {
          const checkout = f?.checkout;
          if (checkout && checkout.codigoCHECKOUT != null) {
            const id = String(checkout.codigoCHECKOUT);
            if (!checkoutMap.has(id)) {
              checkoutMap.set(id, checkout);
            }
          } else if (f?.checkoutFATURA) {
            // Fallback: se a fatura não tem checkout aninhado, criar objeto mínimo
            const id = String(f.checkoutFATURA);
            if (!checkoutMap.has(id)) {
              checkoutMap.set(id, {
                codigoCHECKOUT: f.checkoutFATURA,
                nomeCHECKOUT: f.planoFATURA,
                dataCHECKOUT: f.dataFATURA,
                periodoCHECKOUT: f.periodoFATURA,
                ativoCHECKOUT: f.ativoFATURA,
                plano: f.plano,
                valorCHECKOUT: f.valorFATURA,
                precoCHECKOUT: f.valorFATURA,
                fromInvoice: true
              });
            }
          }
        });
        const mergedCheckouts = Array.from(checkoutMap.values()).map(c => ({
          ...c,
          _fatura: faturasByCheckout[String(c.codigoCHECKOUT)]
        }));

        // Buscar contagem real de anúncios
        const contagemReal = await fetchAnunciosCount();
        
        // Calcular status e validade
        const now = new Date();
        now.setHours(0, 0, 0, 0); // Zera as horas para comparar apenas a data
        
        const formatted = mergedCheckouts.map(item => {
          // Usar data da fatura quando disponível (documento oficial de pagamento)
          const dataAquisicao = new Date(item._fatura?.dataFATURA || item.dataFATURA || item.dataCHECKOUT);
          const validade = new Date(dataAquisicao);
          validade.setDate(validade.getDate() + Number(item.periodoCHECKOUT));
          validade.setHours(23, 59, 59, 999); // Fim do dia de vencimento
          
          let status = '';
          
          // Verifica se foi cancelado
          if (item.ativoCHECKOUT === 5) {
            status = 'Cancelado';
          } else if (item.ativoCHECKOUT === 1) {
            // Se está ativo, verifica se expirou
            if (validade < now) {
              status = 'Expirado';
            } else {
              status = 'Ativo';
            }
          } else {
            // Se não está ativo (ativoCHECKOUT !== 1 e !== 5)
            if (validade < now) {
              status = 'Expirado';
            } else {
              status = 'Aguardando Pagamento';
            }
          }

          // Usar contagem real de anúncios do frontend, exceto para pacotes de Destaque
          const pacoteId = String(item.codigoCHECKOUT);
          const usadosReal = contagemReal[pacoteId] || 0;
          const isDestaquePkg = (item.plano?.tipoPLANO === 5) ||
            /destaque/i.test(item.plano?.nomePLANO || '') ||
            /destaque/i.test(item.plano?.tipoPlano?.planoTIPOPLANO || '');
          const usadosFinal = isDestaquePkg ? (item.utilizados || 0) : usadosReal;
          
          return {
            id: item.codigoCHECKOUT,
            nome: `${item.plano?.tipoPlano?.planoTIPOPLANO || ''} - ${item.nomeCHECKOUT}`,
            dataAquisicao: dataAquisicao.toLocaleDateString(),
            dataAquisicaoISO: dataAquisicao.toISOString(),
            validade: validade.toLocaleDateString(),
            status,
            quantidade: item.plano?.quantidadePLANO || item.quantidadeCHECKOUT || 0,
            // Usar contagem real se > 0, senão usar valor da API
            usados: usadosFinal,
            tipoPLANO: item.plano?.tipoPLANO ?? null,
            tipoLabel: resolvePackageTypeLabel(item.plano, item),
            raw: item
          };
        });
        setAcquiredPackages(formatted);
      } catch (error) {
        console.error('Erro ao buscar pacotes adquiridos:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchPackages();
  }, []);

  // Removido: useEffect de atualização - o backend deve retornar valores corretos
  const token = localStorage.getItem('auth_token');
      
  useEffect(() => {
    async function fetchPlans() {
      try {
        const response = await fetch(`${import.meta.env.VITE_URL_API}/planos`,  {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        // Helper para normalizar nomes de grupos
        const norm = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
        const mapTipoLabel = (raw) => {
          const n = norm(raw);
          if (n.includes('prof')) return 'Profissional';
          if (n.includes('part')) return 'Particular';
          if (n.includes('vacation') || n.includes('feria')) return 'Ferias';
          if (n.includes('desta')) return 'Destaque';
          return raw || 'Outro';
        };

        // Agrupa planos por tipo e ordena pelo campo ordemPLANO
        const grouped = {};
        const coerceFeatureToString = (item) => {
          if (typeof item === 'string') return item;
          if (item == null) return '';
          if (typeof item === 'number' || typeof item === 'boolean') return String(item);
          if (typeof item === 'object') {
            const candidate =
              item.descritivoPLANO ??
              item.descritivo ??
              item.texto ??
              item.name ??
              item.label ??
              item.title ??
              item.description ??
              item.descricao ??
              '';
            return String(candidate);
          }
          return String(item);
        };
        const extractDaysFromText = (arr = []) => {
          for (const item of arr) {
            const str = coerceFeatureToString(item);
            const m = str.match(/(\d+)\s*dias?/i);
            if (m) return Number(m[1]);
          }
          return undefined;
        };
        data.forEach(plan => {
          const tipo = mapTipoLabel(plan.tipoPlano?.planoTIPOPLANO || 'Outro');
          if (!grouped[tipo]) grouped[tipo] = [];
          const normalizedQuantity = normalizePlanQuantity(plan);
          const rawFeatures = Array.isArray(plan.descritivosPLANO) ? plan.descritivosPLANO : [];
          const normalizedFeatures = rawFeatures
            .map(coerceFeatureToString)
            .map((s) => (typeof s === 'string' ? s.trim() : s))
            .filter(Boolean);
          const tempoDias = Number.isFinite(Number(plan.tempoPLANO)) ? Number(plan.tempoPLANO) : extractDaysFromText(rawFeatures);
          const qtyLabel = normalizedQuantity === 1 ? 'imóvel' : 'imóveis';
          grouped[tipo].push({
            stripeid: plan.stripeidPLANO,
            codigo: plan.codigoPLANO,
            title: plan.nomePLANO.replace(/<br\/?/g, ' '),
            subtitle: buildPlanSubtitle({ ...plan, quantidadePLANO: normalizedQuantity }),
            quantidade: normalizedQuantity,
            tempoDias,
            // Guardar ambos os valores para lógica correta (promo prevalece quando > 0)
            valorPLANO: parseFloat(plan.valorPLANO),
            valorPromocionalPLANO: plan.valorPromocionalPLANO != null ? parseFloat(plan.valorPromocionalPLANO) : 0,
            bestSeller: plan.destacarPLANO === 1,
            ordem: plan.ordemPLANO || 9999,
            features: normalizedFeatures.length > 0 ? normalizedFeatures : [
              `Período de tempo: ${plan.tempoPLANO} Dias`,
              `Publique até ${normalizedQuantity}\u00a0${qtyLabel}`,
              'Taxa de câmbio não incluída',
              '% IVA não incluído',
              'Visibilidade em + de 50 países',
              'Sem obrigatoriedade de contrato'
            ]
          });
        });
        // Para ADMIN, garantir presença de todos os grupos esperados
        if (role === 'ADMIN') {
          ['Particular','Profissional','Ferias','Destaque'].forEach(key => {
            if (!grouped[key]) grouped[key] = [];
          });
        }
        // Remover planos "10.300 Imóveis" e adicionar planos de oferta personalizada
        Object.keys(grouped).forEach(tipo => {
          // Filtrar planos com quantidade 10300
          grouped[tipo] = grouped[tipo].filter(plan => plan.quantidade !== 10300);

          // Adicionar planos de oferta personalizada para Profissional
          if (tipo === 'Profissional') {
            grouped[tipo].push({
              stripeid: null,
              codigo: 'custom_offer_1',
              title: 'Plano PRO MAX',
              subtitle: 'Solicitar oferta personalizada',
              quantidade: 0,
              tempoDias: 30,
              valorPLANO: 0,
              valorPromocionalPLANO: 0,
              bestSeller: false,
              ordem: 9999,
              features: ['Entre em contato para negociar um plano personalizado'],
              isCustomOffer: true
            });
            grouped[tipo].push({
              stripeid: null,
              codigo: 'custom_offer_2',
              title: 'Plano BUSINESS +',
              subtitle: 'Solicitar orçamento personalizado',
              quantidade: 0,
              tempoDias: 365,
              valorPLANO: 0,
              valorPromocionalPLANO: 0,
              bestSeller: false,
              ordem: 9999,
              features: ['Entre em contato para negociar um plano personalizado'],
              isCustomOffer: true
            });
          }
        });

        // Ordena cada grupo: primeiro por período (menor primeiro), depois por quantidade
        Object.keys(grouped).forEach(tipo => {
          grouped[tipo].sort((a, b) => {
            // Ordenar por período (menor primeiro)
            const periodA = a.tempoDias || 0;
            const periodB = b.tempoDias || 0;
            if (periodA !== periodB) {
              return periodA - periodB;
            }

            // Dentro do mesmo período, ofertas personalizadas ficam por último
            if (a.isCustomOffer && !b.isCustomOffer) return 1;
            if (!a.isCustomOffer && b.isCustomOffer) return -1;
            if (a.isCustomOffer && b.isCustomOffer) return 0;

            // Se período igual, ordenar por quantidade
            const qtyA = a.quantidade || 0;
            const qtyB = b.quantidade || 0;
            return qtyA - qtyB;
          });
        });
        // Restringe por perfil
        let filtered = grouped;
        if (role === 'ADMIN') {
          // ADMIN vê todos os planos sem restrições (inclui 'Destaque')
          filtered = grouped;
        } else if (role === 'PARTICULAR') {
          // PARTICULAR: ocultar planos profissionais
          filtered = {};
          Object.keys(grouped).forEach(tipo => {
            const t = norm(tipo);
            if (t !== 'profissional' && t !== 'professional' && t !== 'prof') {
              filtered[tipo] = grouped[tipo];
            }
          });
          // Particular ainda mantém regra de ocultar 'Destaque' sem pacote adquirido
          if (filtered['Destaque'] && acquiredPackages.length === 0) {
            const { Destaque, ...rest } = filtered;
            filtered = rest;
          }
        } else {
          // PROFISSIONAL/EMPREENDIMENTO: ocultar planos 'Particular'
          if (role === 'PROFISSIONAL' || role === 'EMPREENDIMENTO') {
            const { Particular, ...rest } = grouped;
            filtered = rest;
          }
          // Para outros perfis (ou após remoção acima), ocultar 'Destaque' se não houver nenhum pacote adquirido
          if (filtered['Destaque'] && acquiredPackages.length === 0) {
            const { Destaque, ...rest } = filtered;
            filtered = rest;
          }
        }
        setPlans(filtered);
        // Define a aba padrão
        // Ordena abas em ordem preferida
        const order = ['Particular','Profissional','Ferias','Destaque'];
        const tabs = Object.keys(filtered).sort((a,b) => {
          const ia = order.indexOf(a);
          const ib = order.indexOf(b);
          return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        });
        
        // Verifica se há parâmetro tab na URL para abrir diretamente na aba específica
        const urlParams = new URLSearchParams(location.search);
        const urlTab = urlParams.get('tab');
        
        // Prioriza o parâmetro da URL se for válido
        if (urlTab && filtered[urlTab]) {
          setSelectedTab(urlTab);
        } else if (!tabs.includes(selectedTab)) {
          let nextTab = tabs[0] || '';
          if (role === 'PARTICULAR' && filtered['Particular']) nextTab = 'Particular';
          if ((role === 'PROFISSIONAL' || role === 'EMPREENDIMENTO') && filtered['Profissional']) nextTab = 'Profissional';
          setSelectedTab(nextTab);
        }
        // Debug auxiliar no console (pode remover depois)
        try {
          console.log('[MyPackages] role:', role);
          console.log('[MyPackages] grouped keys:', Object.keys(grouped));
          console.log('[MyPackages] filtered keys:', Object.keys(filtered));
        } catch (e) { /* ignore */ }
      } catch (error) {
        console.error('Erro ao buscar planos:', error);
      }
    }
    fetchPlans();
  }, [role, acquiredPackages, buildPlanSubtitle, selectedTab, token, location]);

  // Remove um pacote pendente da lista (descartar visualmente)
  function HandleDismissPending(pkg) {
    if (!pkg) return;
    const ok = window.confirm('Remover este pacote pendente da lista?');
    if (!ok) return;
    setAcquiredPackages((prev) => prev.filter((p) => p.id !== pkg.id));
  }

  // Cancela/remover pendente via API e atualiza UI
  async function handleDeletePending(pkg) {
    if (!pkg) return;
    const nome = pkg?.nome || 'este pacote';
    openConfirm('Tem certeza?', `Deseja realmente excluir o pacote "${nome}"? Esta ação não poderá ser desfeita!`, async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${import.meta.env.VITE_URL_API}/checkouts/${pkg.id}`, {
          method: 'PATCH',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ativoCHECKOUT: 5 }),
        });
        if (!res.ok) throw new Error('Falha ao cancelar pacote');
        setAcquiredPackages((prev) => prev.filter((p) => p.id !== pkg.id));
        showAlert('Pacote removido', 'O pacote pendente foi cancelado/removido.');
      } catch (err) {
        console.error(err);
        showAlert('Erro ao remover', 'Não foi possível remover o pacote pendente.');
      } finally {
        closeConfirm();
      }
    });
  }

  // Removido: useEffect do freePlanRedeemed - não necessário para correção da contagem

  async function handleRenew(pkg) {
    // Busca o plano correspondente ao pacote em todos os grupos
    const planoId = pkg.raw?.planoCHECKOUT;
    const allPlans = Object.values(plans).flat();
    const plano = allPlans.find(p => Number(p.codigo) === Number(planoId));
    if (!plano) {
      showAlert('Plano não encontrado', 'Entre em contato com o suporte para renovar este pacote.');
      return;
    }
    // Reutiliza o mesmo fluxo de checkout
    if (typeof window.handlePlanSelection === 'function') {
      window.handlePlanSelection(plano);
    } else if (typeof handlePlanSelection === 'function') {
      handlePlanSelection(plano);
    } else {
      // fallback: tenta redirecionar para rota de pagamento
      window.location.href = '/dashboard/payments?plano=' + encodeURIComponent(pkg.nome);
    }
  }

  // Abre Stripe para pagar um pacote pendente (Aguardando Pagamento)
  async function handlePayPending(pkg) {
    try {
      const raw = pkg?.raw || {};
      // Tenta obter o priceId diretamente do checkout; caso não exista, procura pelo plano correspondente
      const pickFirst = (obj, keys) => keys.map(k => obj?.[k]).find(v => !!v);
      let priceId = pickFirst(raw, [
        'stripeidCHECKOUT',
        'stripePriceCHECKOUT',
        'stripe_price_id',
        'stripePriceId',
        'price_id',
        'priceId',
        'stripe_price',
        'stripePrice'
      ]) || pickFirst(raw?.plano || {}, [
        'stripeidPLANO',
        'stripe_price_id',
        'stripePriceId',
        'stripeId',
        'stripe_price',
        'stripePrice'
      ]);
      if (!priceId) {
        const allPlans = Object.values(plans || {}).flat();
        const norm = s => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const baseValue = p => (Number(p?.valorPromocionalPLANO || 0) > 0 ? Number(p.valorPromocionalPLANO) : Number(p?.valorPLANO || 0));
        const isFreePlan = p => baseValue(p) === 0;

        // 1) Preferência: código do plano vindo do checkout
        const candidateIds = [
          raw.planoCHECKOUT,
          raw.codigoPLANO,
          raw.plano_id,
          raw?.plano?.codigoPLANO,
          raw?.plano?.id
        ].filter(v => v != null);
        let plano = null;
        for (const pid of candidateIds) {
          plano = allPlans.find(p => Number(p.codigo) === Number(pid));
          if (plano) { console.log('[MyPackages] handlePayPending: matched by codigo', pid, plano); break; }
        }

        // 2) Fallback: match EXATO por título
        if (!plano) {
          const nome = raw.nomeCHECKOUT || raw.nome || raw?.plano?.nomePLANO || pkg?.nome;
          if (nome) {
            const n = norm(nome);
            plano = allPlans.find(p => norm(p.title) === n);
            if (plano) console.log('[MyPackages] handlePayPending: matched by exact title', nome, plano);
          }
        }

        // 3) Fallback adicional: mesmo valor do checkout (quando disponível)
        if (!plano) {
          const checkoutAmount = Number(raw.valorCHECKOUT || raw.valor || raw.amount || raw.total);
          if (!Number.isNaN(checkoutAmount) && checkoutAmount > 0) {
            const byAmount = allPlans.filter(p => Number(baseValue(p).toFixed(2)) === Number(checkoutAmount.toFixed(2)));
            if (byAmount.length === 1) {
              plano = byAmount[0];
              console.log('[MyPackages] handlePayPending: matched by amount', checkoutAmount, plano);
            }
          }
        }

        // 4) Fallback adicional: mesma quantidade e não gratuito
        if (!plano) {
          const qtd = raw?.plano?.quantidadePLANO || raw.quantidadeCHECKOUT || pkg?.quantidade;
          if (qtd != null) {
            const candidates = allPlans.filter(p => Number((p.subtitle || '').match(/\d+/)?.[0]) === Number(qtd) && !isFreePlan(p));
            if (candidates.length === 1) {
              plano = candidates[0];
              console.log('[MyPackages] handlePayPending: matched by quantity non-free', qtd, plano);
            }
          }
        }

        if (plano?.stripeid) priceId = plano.stripeid;
      }

      // 5) Fallback definitivo: buscar planos diretamente na API e casar por código
      if (!priceId) {
        try {
          const token = localStorage.getItem('auth_token');
          const res = await fetch(`${import.meta.env.VITE_URL_API}/planos`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          const apiPlans = await res.json();
          const planoId = raw.planoCHECKOUT || raw.codigoPLANO || raw.plano_id || raw?.plano?.codigoPLANO || raw?.plano?.id;
          const found = (apiPlans || []).find(p => Number(p.codigoPLANO) === Number(planoId));
          if (found?.stripeidPLANO) {
            priceId = found.stripeidPLANO;
            console.log('[MyPackages] handlePayPending: matched by API fetch codigo', planoId, found);
          }
        } catch (e) {
          console.warn('[MyPackages] handlePayPending: fallback API fetch failed', e);
        }
      }
      if (!priceId) {
        const flatPlans = Object.values(plans || {}).flat();
        window.__checkoutDebug = { raw, plans: flatPlans };
        console.log('[MyPackages] Checkout RAW:', raw);
        console.log('[MyPackages] Plans flat list:', flatPlans);
        showAlert('Checkout indisponível', 'Não foi possível localizar o identificador de pagamento (Stripe) para este pacote. Abra o console (F12) e veja window.__checkoutDebug para os dados.');
        return;
      }
      const { createCheckoutSession } = await import('../../../services/stripe');
      const session = await createCheckoutSession(priceId, null, {
        page: '/dashboard/packages',
        t: Date.now()
      });
      if (session?.url) {
        window.location.href = session.url;
      } else {
        showAlert('Falha no pagamento', 'Erro ao criar sessão de pagamento. Tente novamente em instantes.');
        console.error('Sessão Stripe inválida:', session);
      }
    } catch (err) {
      showAlert('Erro ao abrir pagamento', `Erro ao abrir pagamento: ${err?.message || err}`);
      console.error('handlePayPending error:', err);
    }
  }

  async function handlePlanSelection(plan) {
    try {
      // Tratar planos de oferta personalizada - redirecionar para página de contato
      if (plan.isCustomOffer) {
        window.location.href = '/contato';
        return;
      }

      // Detecta plano gratuito considerando valor promocional quando > 0
      const baseValue = Number(plan?.valorPromocionalPLANO || 0) > 0
        ? Number(plan.valorPromocionalPLANO)
        : Number(plan?.valorPLANO || 0);
      const isFree = baseValue === 0;
      if (isFree) {
        // Se o usuário tem perfil 'Profissional', o plano gratuito não está disponível
        if (role === 'PROFISSIONAL') {
          showAlert('Plano Indisponível', 'O Plano Gratuito está disponível apenas para contas Particulares.');
          return;
        }

        // Verificação simples de plano gratuito já resgatado
        const alreadyRedeemed = localStorage.getItem('free_plan_redeemed') === '1';
        if (alreadyRedeemed) {
          showAlert('Plano gratuito já resgatado', 'Você já utilizou o plano gratuito. Cada conta pode resgatar apenas uma vez.');
          return;
        }
      }
      let priceId = plan.stripeid;
      if (!priceId) {
        showAlert('Plano indisponível', 'ID do Stripe não encontrado para este plano.');
        return;
      }
      // Marca flags no frontend para bloqueio estrito
      if (isFree) {
        try {
          localStorage.setItem('free_plan_pending', '1');
          localStorage.setItem('free_plan_redeemed', '1');
        } catch (e) { /* ignore */ }
      }
      // Usa o serviço stripe.js para criar a sessão (import dinâmico)
      const { createCheckoutSession } = await import('../../../services/stripe');
      const session = await createCheckoutSession(priceId, null, {
        page: '/dashboard/packages',
        t: Date.now()
      });
      if (session?.url) {
        window.location.href = session.url;
      } else {
        showAlert('Falha no pagamento', 'Erro ao criar sessão de pagamento. Tente novamente em instantes.');
        console.error('Erro ao criar sessão de pagamento:', session);
      }
    } catch (error) {
      showAlert('Erro ao processar pagamento', `Erro ao processar pagamento: ${error.message || error}`);
      console.error('Erro ao processar pagamento:', error);
    }
  }

  // Resolve o rótulo do tipo de plano a partir de várias fontes
  const resolvePackageTypeLabel = (plano, checkout) => {
    const rawTipo = String(plano?.tipoPlano?.planoTIPOPLANO || '').toLowerCase();
    const rawNome = String(checkout?.nomeCHECKOUT || plano?.nomePLANO || '').toLowerCase();
    const candidates = [rawTipo, rawNome].filter(Boolean);
    for (const str of candidates) {
      if (str.includes('destaque')) return 'Destaque';
      if (str.includes('particular')) return 'Particular';
      if (str.includes('profissional') || str.includes('pro')) return 'Profissional';
      if (str.includes('ferias') || str.includes('férias')) return 'Férias';
    }
    const id = Number(plano?.tipoPLANO);
    if (id === 5) return 'Destaque';
    if (id === 2) return 'Particular';
    if (id === 3) return 'Profissional';
    if (id === 4) return 'Férias';
    return '';
  };

  const getPlanTypeLabel = (pkg) => {
    if (pkg.tipoLabel) return pkg.tipoLabel;
    const nome = String(pkg.nome || '').toLowerCase();
    if (nome.includes('destaque')) return 'Destaque';
    if (nome.includes('particular')) return 'Particular';
    if (nome.includes('profissional') || nome.includes('pro')) return 'Profissional';
    if (nome.includes('ferias') || nome.includes('férias')) return 'Férias';
    return '';
  };

  // Ordenar por data de aquisição decrescente (mais recentes primeiro)
  const sortByDate = (a, b) => new Date(b.dataAquisicaoISO || b.raw?.dataAquisicaoISO) - new Date(a.dataAquisicaoISO || a.raw?.dataAquisicaoISO);
  const typeFilterFn = (p) => !typeFilter || getPlanTypeLabel(p) === typeFilter;
  const acquiredActive = acquiredPackages.filter(p => p.status === 'Ativo' && typeFilterFn(p)).sort(sortByDate);
  const acquiredExpired = acquiredPackages.filter(p => p.status === 'Expirado' && typeFilterFn(p)).sort(sortByDate);
  const acquiredPending = acquiredPackages.filter(p => p.status === 'Aguardando Pagamento' && typeFilterFn(p)).sort(sortByDate);
  const acquiredCancelled = acquiredPackages.filter(p => p.status === 'Cancelado' && typeFilterFn(p)).sort(sortByDate);
  const availableTypes = Array.from(new Set(acquiredPackages.map(getPlanTypeLabel).filter(Boolean))).sort();

  // Resumo e filtros com base nos pacotes adquiridos
  const resumo = {
    total: acquiredPackages.filter(typeFilterFn).length,
    ativos: acquiredPackages.filter(p => p.status === 'Ativo' && typeFilterFn(p)).length,
    pendentes: acquiredPackages.filter(p => p.status === 'Aguardando Pagamento' && typeFilterFn(p)).length,
    expirados: acquiredPackages.filter(p => p.status === 'Expirado' && typeFilterFn(p)).length,
    cancelados: acquiredPackages.filter(p => p.status === 'Cancelado' && typeFilterFn(p)).length,
  };

  const paginate = (list, page) => list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const renderPagination = (total, page, setPage) => {
    const totalPages = Math.ceil(total / PAGE_SIZE);
    if (totalPages <= 1) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: page === 1 ? '#f9fafb' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
        >
          Anterior
        </button>
        <span style={{ alignSelf: 'center', color: '#6b7280', fontSize: 13 }}>Página {page} de {totalPages}</span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: page === totalPages ? '#f9fafb' : '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
        >
          Próxima
        </button>
      </div>
    );
  };

  // Garante página válida quando total de itens muda
  useEffect(() => {
    const clamp = (total, page, setter) => {
      const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
      if (page > totalPages) setter(totalPages);
      if (page < 1) setter(1);
    };
    clamp(acquiredActive.length, activePage, setActivePage);
    clamp(acquiredPending.length, pendingPage, setPendingPage);
    clamp(acquiredExpired.length, expiredPage, setExpiredPage);
    clamp(acquiredCancelled.length, cancelledPage, setCancelledPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acquiredActive.length, acquiredPending.length, acquiredExpired.length, acquiredCancelled.length]);

  // Reseta páginas quando os filtros de status/tipo mudam
  useEffect(() => {
    setActivePage(1);
    setPendingPage(1);
    setExpiredPage(1);
    setCancelledPage(1);
  }, [statusFilter, typeFilter]);

  const cleanPlanTitle = (title) => String(title || '')
    .replace(/\s*>\s*\d+\s*im[oó]ve(?:l|is)/gi, '')
    .replace(/\s+\d+\s*im[oó]ve(?:l|is)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <PageContainer>
      <Header>
        <h1>Meus Pacotes</h1>
      </Header>

      {/* PLANOS DISPONÍVEIS */}
      <h2 style={{ fontSize: '1.2rem', marginBottom: 14, color: '#34574A' }}>Planos disponíveis</h2>
      {/* Tabs de Tipos de Plano */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        {Object.keys(plans).map(tipo => (
          <button
            key={tipo}
            style={{
              padding: '8px 22px',
              borderRadius: 8,
              border: selectedTab === tipo ? '2px solid #ba824f' : '1.5px solid #e0b07c',
              background: selectedTab === tipo ? '#ba824f' : '#fff',
              color: selectedTab === tipo ? '#fff' : '#ba824f',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all .18s',
            }}
            onClick={() => setSelectedTab(tipo)}
          >
            {tipo}
          </button>
        ))}
      </div>
      <PlansScroll>
        {selectedTab && plans[selectedTab] && (
          <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', color: '#555' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '1rem' }}>Plano</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '1rem' }}>Quantidade</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '1rem' }}>Período</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '1rem' }}>Preço</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', fontSize: '1rem' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {plans[selectedTab].map((plan, idx) => (
                <tr
                  key={idx}
                  style={{
                    background: idx % 2 === 0 ? '#fff' : '#f9f9f9',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f1f1')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f9f9f9')}
                >
                  <td style={{ padding: '12px', fontSize: '0.95rem', color: '#333' }}>{cleanPlanTitle(plan.title)}</td>
                  <td style={{ padding: '12px', fontSize: '0.95rem', color: '#555' }}>{plan.isCustomOffer ? '—' : plan.subtitle}</td>
                  <td style={{ padding: '12px', fontSize: '0.95rem', color: '#555' }}>
                    {(() => {
                      const periodRaw = plan.tempoDias ?? plan.tempoPLANO ?? plan.periodoPLANO ?? plan.periodo ?? plan.duracao ?? plan.duration ?? plan.validadePLANO;
                      if (periodRaw === 0 || periodRaw === '0' || periodRaw === null || periodRaw === undefined || periodRaw === '') return '—';
                      const num = Number(periodRaw);
                      if (Number.isFinite(num)) return `${num} dia${num === 1 ? '' : 's'}`;
                      return String(periodRaw);
                    })()}
                  </td>
                  <td style={{ padding: '12px', fontSize: '0.95rem', color: '#ba824f', fontWeight: '700' }}>
                    {plan.isCustomOffer ? '—' : (
                      `€ ${(
                        (Number(plan?.valorPromocionalPLANO || 0) > 0
                          ? Number(plan.valorPromocionalPLANO)
                          : Number(plan?.valorPLANO || 0))
                      ).toFixed(2)}`
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <PlanButton
                      onClick={() => handlePlanSelection(plan)}
                      disabled={plan.isCustomOffer ? false : (
                        // Bloqueio de plano gratuito para PROFISSIONAL (exceto ADMIN)
                        ((Number(plan?.valorPromocionalPLANO || 0) > 0 ? Number(plan.valorPromocionalPLANO) : Number(plan?.valorPLANO || 0)) === 0)
                        && role === 'PROFISSIONAL'
                      ) || (
                        // Bloqueio de plano gratuito já resgatado (exceto ADMIN)
                        ((Number(plan?.valorPromocionalPLANO || 0) > 0 ? Number(plan.valorPromocionalPLANO) : Number(plan?.valorPLANO || 0)) === 0)
                        && localStorage.getItem('free_plan_redeemed') === '1'
                        && role !== 'ADMIN'
                      ) || (
                        // Bloqueio de Destaque sem pacote (exceto ADMIN)
                        selectedTab === 'Destaque' && acquiredPackages.length === 0 && role !== 'ADMIN'
                      )}
                      title={(() => {
                        if (plan.isCustomOffer) return 'Solicitar proposta personalizada';
                        const isFree = (Number(plan?.valorPromocionalPLANO || 0) > 0 ? Number(plan.valorPromocionalPLANO) : Number(plan?.valorPLANO || 0)) === 0;
                        if (isFree && role === 'PROFISSIONAL') return 'O Plano Gratuito está disponível apenas para contas Particulares';
                        if (isFree && localStorage.getItem('free_plan_redeemed') === '1' && role !== 'ADMIN') return 'Plano Gratuito já resgatado';
                        if (selectedTab === 'Destaque' && acquiredPackages.length === 0 && role !== 'ADMIN') return 'Adquira um plano antes de comprar destaque';
                        return 'Escolher Plano';
                      })()}
                    >
                      {(() => {
                        if (plan.isCustomOffer) return 'Solicitar Proposta Personalizada';
                        const isFree = (Number(plan?.valorPromocionalPLANO || 0) > 0 ? Number(plan.valorPromocionalPLANO) : Number(plan?.valorPLANO || 0)) === 0;
                        if (isFree && role === 'PROFISSIONAL') return 'Indisponível';
                        if (isFree && localStorage.getItem('free_plan_redeemed') === '1' && role !== 'ADMIN') return 'Plano Gratuito já resgatado';
                        if (selectedTab === 'Destaque' && acquiredPackages.length === 0 && role !== 'ADMIN') return 'Bloqueado: adquira um plano';
                        return 'Escolher Plano';
                      })()}
                    </PlanButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PlansScroll>
      {/* Aviso para destaque bloqueado (exceto ADMIN) */}
      {selectedTab === 'Destaque' && acquiredPackages.length === 0 && role !== 'ADMIN' && (
        <div style={{ marginTop: 12, color: '#c62828' }}>
          Para comprar destaque, primeiro adquira um plano Particular.
        </div>
      )}

      {/* LISTA DE PACOTES ADQUIRIDOS */}
      <div style={{ marginTop: 28, marginBottom: 38 }}>
        <h2 style={{ fontSize: '1.2rem', marginBottom: 14, color: '#34574A' }}>Pacotes adquiridos</h2>

        {/* ABAS DE STATUS */}
        <div style={{ display: 'flex', gap: 8, borderBottom: '2px solid #e5e7eb', marginBottom: 18, flexWrap: 'wrap' }}>
          {[
            { key: '', label: 'Todos', color: '#34574A' },
            { key: 'Ativo', label: 'Ativos', color: '#388e3c' },
            { key: 'Aguardando Pagamento', label: 'Pendentes', color: '#c47f17' },
            { key: 'Expirado', label: 'Expirados', color: '#c62828' },
            { key: 'Cancelado', label: 'Cancelados', color: '#757575' },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setStatusFilter(item.key)}
              style={{
                padding: '10px 18px',
                borderRadius: '8px 8px 0 0',
                border: '2px solid transparent',
                borderBottom: statusFilter === item.key ? `2px solid ${item.color}` : '2px solid transparent',
                background: statusFilter === item.key ? '#fff' : 'transparent',
                color: statusFilter === item.key ? item.color : '#6b7280',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.95rem',
                transition: 'all .18s',
                marginBottom: -2,
              }}
            >
              {item.label} ({resumo[item.key === '' ? 'total' : item.label.toLowerCase().replace(' ', '')]})
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#34574A', fontSize: '0.95rem' }}>Filtrar por tipo:</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e0b07c', fontSize: '0.95rem', color: '#34574A', background: '#fff', minWidth: 160 }}
          >
            <option value="">Todos os tipos</option>
            {availableTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {typeFilter && (
            <button
              type="button"
              onClick={() => setTypeFilter('')}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              Limpar tipo
            </button>
          )}
        </div>
        {Loading ? (
          <div style={{ color: '#888', fontSize: '1.1rem', padding: '18px 0' }}>Carregando...</div>
        ) : acquiredPackages.length === 0 ? (
          <div style={{ color: '#888', fontSize: '1.1rem', padding: '18px 0' }}>Você ainda não adquiriu nenhum pacote.</div>
        ) : (
          <>
            {acquiredActive.length > 0 && (!statusFilter || statusFilter === 'Ativo') && (
              <SectionBox>
                <div style={{ fontWeight: 600, color: '#388e3c', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaCheckCircle color="#388e3c" /> Pacotes Ativos
                </div>
                {paginate(acquiredActive, activePage).map(pkg => (
                  <PackageRow key={pkg.id} active style={{ fontSize: '0.95rem', padding: '10px 12px', minHeight: 40 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <StatusBadge active style={{ fontSize: '0.85rem', padding: '3px 12px' }}><FaCheckCircle size={14} /> Ativo</StatusBadge>
                        <span style={{ fontWeight: 600, color: '#34574A', fontSize: '1rem', marginLeft: 8 }}>{pkg.nome}</span>
                      </div>
                      {getPlanTypeLabel(pkg) && (
                        <div style={{ fontSize: '0.75rem', color: '#BA824F', fontWeight: '500', marginTop: '2px', marginLeft: '2px' }}>
                          {getPlanTypeLabel(pkg)}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 2, marginLeft: 2 }}>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Aquisição: <b style={{ color: '#34574A' }}>{pkg.dataAquisicao}</b></span>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Validade: <b style={{ color: '#34574A' }}>{pkg.validade}</b></span>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Utilizados: <b style={{ color: '#34574A' }}>{pkg.usados} de {pkg.quantidade}</b></span>
                      </div>
                    </div>
                    <PackageActions style={{ alignSelf: 'center', display: 'flex', gap: 8 }}>
                      <DetailsButton
                        style={{ fontSize: '0.92rem', padding: '6px 14px' }}
                        onClick={() => setModalPkg(pkg)}
                      >
                        <FaHome size={14} /> Ver detalhes
                      </DetailsButton>
                      {showDeleteButtons && (
                        <DetailsButton
                          style={{ fontSize: '0.92rem', padding: '6px 12px', background: '#ffe6e6', color: '#b91c1c', border: '1px solid #f5c2c7' }}
                          onClick={() => handleDeletePending(pkg)}
                          title="Cancelar/remover este pacote"
                        >
                          <FaTrashAlt size={14} />
                        </DetailsButton>
                      )}
                    </PackageActions>
                  </PackageRow>
                ))}
                {renderPagination(acquiredActive.length, activePage, setActivePage)}
              </SectionBox>
            )}
            {acquiredPending.length > 0 && (!statusFilter || statusFilter === 'Aguardando Pagamento') && (
              <SectionBox>
                <div style={{ fontWeight: 600, color: '#c47f17', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaRegClock color="#c47f17" /> Pacotes Aguardando Pagamento
                </div>
                {paginate(acquiredPending, pendingPage).map(pkg => (
                  <PackageRow key={pkg.id} style={{ fontSize: '0.95rem', padding: '10px 12px', minHeight: 40, opacity: .95 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <StatusBadge style={{ background: '#fff4e5', color: '#c47f17', border: '1px solid #f6d9a8', fontSize: '0.85rem', padding: '3px 12px' }}><FaRegClock size={14} /> Aguardando Pagamento</StatusBadge>
                        <span style={{ fontWeight: 600, color: '#8a5a14', fontSize: '1rem', marginLeft: 8 }}>{pkg.nome}</span>
                      </div>
                      {getPlanTypeLabel(pkg) && (
                        <div style={{ fontSize: '0.75rem', color: '#BA824F', fontWeight: '500', marginTop: '2px', marginLeft: '2px' }}>
                          {getPlanTypeLabel(pkg)}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 2, marginLeft: 2 }}>
                        <span style={{ color: '#8a5a14', fontSize: '0.90rem' }}>Aquisição: <b style={{ color: '#8a5a14' }}>{pkg.dataAquisicao}</b></span>
                        <span style={{ color: '#8a5a14', fontSize: '0.90rem' }}>Validade: <b style={{ color: '#8a5a14' }}>{pkg.validade}</b></span>
                        <span style={{ color: '#8a5a14', fontSize: '0.90rem' }}>Utilizados: <b style={{ color: '#8a5a14' }}>{pkg.usados} de {pkg.quantidade}</b></span>
                      </div>
                    </div>
                    <PackageActions style={{ alignSelf: 'center', display: 'flex', gap: 8 }}>
                      <DetailsButton
                        style={{ fontSize: '0.92rem', padding: '6px 14px' }}
                        onClick={() => setModalPkg(pkg)}
                      >
                        <FaHome size={14} /> Ver detalhes
                      </DetailsButton>
                      <RenewButton
                        style={{ fontSize: '0.92rem', padding: '6px 14px' }}
                        onClick={() => handlePayPending(pkg)}
                      >
                        Pagar
                      </RenewButton>
                      {showDeleteButtons && (
                        <DetailsButton
                          style={{ fontSize: '0.92rem', padding: '6px 12px', background: '#ffe6e6', color: '#b91c1c', border: '1px solid #f5c2c7' }}
                          onClick={() => handleDeletePending(pkg)}
                          title="Cancelar/remover este pacote pendente"
                        >
                          <FaTrashAlt size={14} />
                        </DetailsButton>
                      )}
                    </PackageActions>
                  </PackageRow>
                ))}
                {renderPagination(acquiredPending.length, pendingPage, setPendingPage)}
              </SectionBox>
            )}

            {acquiredExpired.length > 0 && (!statusFilter || statusFilter === 'Expirado') && (
              <SectionBox>
                <div style={{ fontWeight: 600, color: '#c62828', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaRegClock color="#c62828" /> Pacotes Expirados
                </div>
                {paginate(acquiredExpired, expiredPage).map(pkg => (
                  <PackageRow key={pkg.id} style={{ fontSize: '0.95rem', padding: '10px 12px', minHeight: 40 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <StatusBadge style={{ fontSize: '0.85rem', padding: '3px 12px' }}><FaRegClock size={14} /> Expirado</StatusBadge>
                        <span style={{ fontWeight: 600, color: '#b71c1c', fontSize: '1rem', marginLeft: 8 }}>{pkg.nome}</span>
                      </div>
                      {getPlanTypeLabel(pkg) && (
                        <div style={{ fontSize: '0.75rem', color: '#BA824F', fontWeight: '500', marginTop: '2px', marginLeft: '2px' }}>
                          {getPlanTypeLabel(pkg)}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 2, marginLeft: 2 }}>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Aquisição: <b style={{ color: '#b71c1c' }}>{pkg.dataAquisicao}</b></span>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Validade: <b style={{ color: '#b71c1c' }}>{pkg.validade}</b></span>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Utilizados: <b style={{ color: '#b71c1c' }}>{pkg.usados} de {pkg.quantidade}</b></span>
                      </div>
                    </div>
                    <PackageActions style={{ alignSelf: 'center', display: 'flex', gap: 8 }}>
                      {showRenewButtons && (
                        <RenewButton style={{ fontSize: '0.92rem', padding: '6px 14px' }} onClick={() => handleRenew(pkg)}><FaRedo size={14} /> Renovar</RenewButton>
                      )}
                      {showDeleteButtons && (
                        <DetailsButton
                          style={{ fontSize: '0.92rem', padding: '6px 12px', background: '#ffe6e6', color: '#b91c1c', border: '1px solid #f5c2c7' }}
                          onClick={() => handleDeletePending(pkg)}
                          title="Cancelar/remover este pacote"
                        >
                          <FaTrashAlt size={14} />
                        </DetailsButton>
                      )}
                    </PackageActions>
                  </PackageRow>
                ))}
                {renderPagination(acquiredExpired.length, expiredPage, setExpiredPage)}
              </SectionBox>
            )}

            {acquiredCancelled.length > 0 && (!statusFilter || statusFilter === 'Cancelado') && (
              <SectionBox>
                <div style={{ fontWeight: 600, color: '#757575', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaRegClock color="#757575" /> Pacotes Cancelados
                </div>
                {paginate(acquiredCancelled, cancelledPage).map(pkg => (
                  <PackageRow key={pkg.id} style={{ fontSize: '0.95rem', padding: '10px 12px', minHeight: 40, opacity: 0.7 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <StatusBadge style={{ background: '#f5f5f5', color: '#757575', border: '1px solid #e0e0e0', fontSize: '0.85rem', padding: '3px 12px' }}><FaRegClock size={14} /> Cancelado</StatusBadge>
                        <span style={{ fontWeight: 600, color: '#757575', fontSize: '1rem', marginLeft: 8 }}>{pkg.nome}</span>
                      </div>
                      {getPlanTypeLabel(pkg) && (
                        <div style={{ fontSize: '0.75rem', color: '#BA824F', fontWeight: '500', marginTop: '2px', marginLeft: '2px' }}>
                          {getPlanTypeLabel(pkg)}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 2, marginLeft: 2 }}>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Aquisição: <b style={{ color: '#757575' }}>{pkg.dataAquisicao}</b></span>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Validade: <b style={{ color: '#757575' }}>{pkg.validade}</b></span>
                        <span style={{ color: '#888', fontSize: '0.90rem' }}>Utilizados: <b style={{ color: '#757575' }}>{pkg.usados} de {pkg.quantidade}</b></span>
                      </div>
                    </div>
                    <PackageActions style={{ alignSelf: 'center', display: 'flex', gap: 8 }}>
                      <span style={{ color: '#999', fontSize: '0.9rem', fontStyle: 'italic' }}>Plano cancelado</span>
                      {showDeleteButtons && (
                        <DetailsButton
                          style={{ fontSize: '0.92rem', padding: '6px 12px', background: '#ffe6e6', color: '#b91c1c', border: '1px solid #f5c2c7' }}
                          onClick={() => handleDeletePending(pkg)}
                          title="Remover este pacote cancelado"
                        >
                          <FaTrashAlt size={14} />
                        </DetailsButton>
                      )}
                    </PackageActions>
                  </PackageRow>
                ))}
                {renderPagination(acquiredCancelled.length, cancelledPage, setCancelledPage)}
              </SectionBox>
            )}
          </>
        )}
      </div>



      <PackageDetailsModal pkg={modalPkg} onClose={() => setModalPkg(null)} onRenew={handleRenew} />
      {modalAlert && (
        <AlertOverlay onClick={closeAlert}>
          <AlertBox onClick={(e) => e.stopPropagation()}>
            <AlertTitle>{modalAlert.title}</AlertTitle>
            <AlertDescription>{modalAlert.description}</AlertDescription>
            <AlertOkButton onClick={closeAlert}>OK</AlertOkButton>
          </AlertBox>
        </AlertOverlay>
      )}

      {confirmModal && (
        <AlertOverlay onClick={closeConfirm}>
          <AlertBox onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', padding: '30px 28px' }}>
            <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 58, height: 58, borderRadius: '50%', border: '2px solid #e9a14c', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e9a14c', fontSize: 30 }}>!</div>
            </div>
            <AlertTitle style={{ fontSize: '1.8rem', marginBottom: 10 }}>{confirmModal.title || 'Tem certeza?'}</AlertTitle>
            <AlertDescription style={{ fontSize: '1rem', color: '#4b5563', marginBottom: 20 }}>{confirmModal.description}</AlertDescription>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 4 }}>
              <AlertOkButton onClick={closeConfirm} style={{ background: '#2f6b4f', border: 'none' }}>Cancelar</AlertOkButton>
              <AlertOkButton onClick={() => confirmModal.onConfirm && confirmModal.onConfirm()} style={{ background: '#c62828', border: 'none' }}>Sim, excluir</AlertOkButton>
            </div>
          </AlertBox>
        </AlertOverlay>
      )}
    </PageContainer>
  );
};

export default MyPackages;


